import BlockView from 'views/block/base-block-view';
import HtmlImportService from 'services/html-import-service';


///

/**
 * This block fetches a chunk of HTML and drops it in the DOM where the block is located.
 * It currently relies upon the deprecated <link rel="import"> which will have to be replaced by <script type="modules">
 *
 * For testing purposes a query string param of html_import_version can be provided to switch the version in the url on the fly
 *
 * In the url there are a few replacements to specify some information from the learner profile and locales
 * These are wrapped in {{}} in the path or URL
 *
 * {{version}}  - 'develop' if local, 'master' if not local or overriden by the query parameter html_import_version
 * {{language}} - The learner's locale like en or es
 * {{country}}  - Specified in the learner profile like us or gb
 * {{region}}   - Specified in the learner profile like ca or dc. In the US this is the state code
 *
 * @example
 * Fetches a chunk of HTML from https://s3.amazonaws.com/everfi-curriculums/content/master/legal/example-country/example-state/example.en.html
 * Assuming not in local mode
 * - type: html-import
 *   path: 'legal/example-country/example-state/example.en.html'
 *
 * A fully qualified URL can also be provided to fetch from other sources
  * - type: html-import
 *    url: 'https://everfi.com/some.html'
 */
export default class HtmlImport extends BlockView {
  constructor(options) {
    super(options);
    this.url = HtmlImportService.parsePath({path: this.model.get('path'), url: this.model.get('url')});
    this.model.set('url', this.url);
    this.default = this.model.get('default') ? HtmlImportService.parsePath({ path: this.model.get('path'), url: this.model.get('default')}):'';
    this.model.set('default', this.default);
    this.linkLoaded = this.linkLoaded.bind(this);  // bind returns a new function, make sure the new one gets cleaned up in `dispose`
  }
  
  load() {
    fetch(this.model.get('url'))
    .then(res => {
      if(res.status !== 404) {
        this.createLink(this.model.get('url'));
      } else if(this.model.get('default')) {
        this.createLink(this.model.get('default'));
      } else {
        console.error(`The asset on ${this.model.get('url')} doesn't exist, you should provide a default url`);
      }
    })
    .catch(e => {
      /* eslint no-unused-vars: "off" */
      /* eslint no-console: "off" */
      console.error(`Error fetching ${this.model.get('url')}`);
    });
  }

  createLink(url) {
    var link = document.head.querySelector(`link[rel="import"][href="${url}"]`);

    if(link) {
      // network requests for <link rel="import>" are deduped
      // checking on link.import avoids a race condition between 2+ blocks on the same page with the same url
      if(link.import) {
        // The link exists and the load event has fired, so the children are ready to add to the DOM
        this.import(link.import.children);
      } else {
        // the link exists but the load event has not fired, so the children are not ready yet
        link.addEventListener('load', this.linkLoaded, false);
      }
    } else {
      // the link doesn't exist at all, create it, attach the load listener and drop in in the <head>
      link = document.createElement('link');
      link.rel = 'import';
      link.href = url;
      link.addEventListener('load', this.linkLoaded, false);

      // Note: the html-imports polyfill watchs for <link> added to <head> but not <body> for performance reasons
      document.head.appendChild(link);
    }
  }

  import(nodes) {
    // clone each node otherwise the original nodes will disappear from the <link>
    this.el.querySelector('.html-import').append(...Array.from(nodes).map(c => c.cloneNode(true)));
  }

  linkLoaded(e) {
    this.import(e.currentTarget.import.body ? e.currentTarget.import.body.children : e.currentTarget.import.children);
  }

  play() {
    this.readyToGo();
  }

  dispose() {
    var link = document.head.querySelector(`link[rel="import"][href="${this.model.get('url')}"]`);
    if(link) {
      link.removeEventListener('load', this.linkLoaded, false);
    }
    super.dispose();
  }
}